import { FormEvent, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeOnboarding,
  createWebhook,
  getOnboarding,
  listSandboxGoals,
  listWebhooks,
  me,
  patchMe,
  testWebhook,
} from '../api/portal';
import { ApiError } from '../api/client';
import { useT } from '../lib/i18n';
import { isClubLogin, isLeagueLogin } from '../lib/portalRole';

export function SetupPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const onboarding = useQuery({ queryKey: ['portal-onboarding'], queryFn: getOnboarding });
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const hooks = useQuery({ queryKey: ['portal-webhooks'], queryFn: listWebhooks });
  const goals = useQuery({ queryKey: ['portal-sandbox-goals'], queryFn: () => listSandboxGoals() });
  const o = onboarding.data;
  const p = profile.data;
  const hour = o?.first_hour;

  const [origins, setOrigins] = useState('');
  const [url, setUrl] = useState('');
  const [hookId, setHookId] = useState('');
  const [goalId, setGoalId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (p) setOrigins((p.allowed_origins ?? []).join('\n'));
  }, [p]);
  useEffect(() => {
    if (!hookId && hooks.data?.[0]) setHookId(hooks.data[0].id);
  }, [hooks.data, hookId]);
  useEffect(() => {
    if (!goalId && goals.data?.[0]) setGoalId(goals.data[0].id);
  }, [goals.data, goalId]);

  const saveSite = useMutation({
    mutationFn: () => patchMe({
      allowed_origins: origins.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      qc.invalidateQueries({ queryKey: ['portal-onboarding'] });
      setMsg('Site hosts saved.');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const addHook = useMutation({
    mutationFn: () => createWebhook({ url, event_filter: ['goal', 'reel.ready'] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-webhooks'] });
      qc.invalidateQueries({ queryKey: ['portal-onboarding'] });
      setUrl('');
      setMsg('Webhook added. Store the signing secret from the Webhooks page if you need HMAC.');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const replay = useMutation({
    mutationFn: () => testWebhook(hookId, { mode: 'replay', event_id: goalId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-onboarding'] });
      qc.invalidateQueries({ queryKey: ['portal-deliveries'] });
      setMsg(res.ok ? 'Replay delivered. Check your URL / Telegram.' : (res.error || 'Replay failed'));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const finish = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-onboarding'] });
      qc.invalidateQueries({ queryKey: ['portal-me'] });
      setMsg('Setup marked complete.');
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const steps = o?.steps;
  const club = (p?.allowed_teams ?? []).map((t) => t.name).join(', ');

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">{t('setup.kicker')}</div>
        <h1 className="page-title">{t('setup.title')}</h1>
        <p className="page-sub">
          {isClubLogin(p) ? t('setup.subClub') : t('setup.subLeague')}
        </p>
      </div>

      {p?.parent_company_name && (
        <div className="card p-3 mb-4 text-sm text-slate-300">
          {t('setup.clubUnder')} <span className="text-white">{p.parent_company_name}</span>
          {club ? ` · ${t('setup.only')} ${club}` : ''}.
        </div>
      )}
      {p && isLeagueLogin(p) && (
        <div className="card p-3 mb-4 text-sm text-slate-300">
          {t('setup.leagueLogin')}{' '}
          <Link to="/clubs" className="text-white underline-offset-2 hover:underline">{t('setup.clubs')}</Link>.
        </div>
      )}

      {hour && (
        <div className="grid md:grid-cols-4 gap-3 mb-6">
          <Stat label="API calls (last hour)" value={String(hour.requests)} />
          <Stat label="Deliveries OK" value={String(hour.webhook_ok)} />
          <Stat label="Deliveries failed" value={String(hour.webhook_failed)} />
          <Stat label="Last delivery" value={hour.last_delivery_at ? hour.last_delivery_at.slice(11, 19) : '—'} />
        </div>
      )}

      {msg && <div className="card p-3 mb-4 text-sm text-slate-200">{msg}</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        <StepCard n={1} title={t('setup.host')} done={!!steps?.site.done}>
          <p className="text-[12px] text-slate-500 mb-2">The embed only answers from these hosts. Empty = any origin (dev).</p>
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveSite.mutate(); }}>
            <textarea
              className="textarea min-h-[88px] mb-2"
              value={origins}
              onChange={(e) => setOrigins(e.target.value)}
              placeholder="www.club.rs"
            />
            <button className="btn-primary" type="submit" disabled={saveSite.isPending}>Save hosts</button>
            <Link to="/sites" className="btn-ghost ml-2 text-xs">Sites →</Link>
          </form>
        </StepCard>

        <StepCard n={2} title={t('setup.webhook')} done={!!steps?.webhook.done}>
          <p className="text-[12px] text-slate-500 mb-2">HTTP URL, Slack, or Telegram. Filter starts as goal + reel.ready.</p>
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); addHook.mutate(); }}>
            <input className="input mb-2" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… or telegram:-100…" required />
            <button className="btn-primary" type="submit" disabled={addHook.isPending || !url.trim()}>Add webhook</button>
            <Link to="/webhooks" className="btn-ghost ml-2 text-xs">Webhooks →</Link>
          </form>
        </StepCard>

        <StepCard n={3} title={t('setup.replay')} done={!!steps?.sandbox.done}>
          <p className="text-[12px] text-slate-500 mb-2">Pushes a finished archive goal to your destination. Not live traffic.</p>
          <select className="input mb-2" value={hookId} onChange={(e) => setHookId(e.target.value)}>
            <option value="">Pick webhook</option>
            {(hooks.data ?? []).map((h) => (
              <option key={h.id} value={h.id}>{h.kind ?? 'http'} · {h.url}</option>
            ))}
          </select>
          <select className="input mb-2" value={goalId} onChange={(e) => setGoalId(e.target.value)}>
            <option value="">Pick archive goal</option>
            {(goals.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn-primary"
            disabled={!hookId || !goalId || replay.isPending}
            onClick={() => replay.mutate()}
          >
            {replay.isPending ? 'Sending…' : 'Replay to my URL'}
          </button>
        </StepCard>
      </div>

      {o?.kind === 'league' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-4">
          <StepCard n={4} title="12 club portals" done={!!steps?.clubs?.done}>
            <p className="text-[12px] text-slate-500 mb-2">
              {steps?.clubs?.count ?? 0} / {steps?.clubs?.target ?? 12} clubs. Each club is a login, not a row on Matches.
            </p>
            <Link to="/clubs" className="btn-primary text-xs">Open Clubs →</Link>
          </StepCard>
          <StepCard n={5} title="Replay every destination" done={!!steps?.replay_per_destination?.done}>
            <p className="text-[12px] text-slate-500 mb-2">
              {steps?.replay_per_destination?.replayed ?? 0} / {steps?.replay_per_destination?.destinations ?? 0} destinations
              have a test or replay. Ping does not count.
            </p>
            <Link to="/webhooks" className="btn-ghost text-xs">Inbox →</Link>
          </StepCard>
          <StepCard n={6} title="White-label" done={!!steps?.white_label?.done}>
            <p className="text-[12px] text-slate-500 mb-2">
              Turn off Powered by, or set a sponsor logo. Usage → Sponsor on widgets.
            </p>
            <Link to="/usage" className="btn-ghost text-xs">Usage →</Link>
          </StepCard>
          <StepCard n={7} title={t('setup.koloPack')} done={false}>
            <p className="text-[12px] text-slate-500 mb-2">{t('setup.koloPackHint')}</p>
            <Link to="/rounds" className="btn-primary text-xs">{t('setup.openRounds')}</Link>
          </StepCard>
        </div>
      )}

      {o?.kind === 'club' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <StepCard n={4} title="One widget key" done={!!steps?.widget?.done}>
            <p className="text-[12px] text-slate-500 mb-2">
              {steps?.widget?.keys ?? 0} widget key{steps?.widget?.keys === 1 ? '' : 's'}. Issue from API keys, then embed on the host.
            </p>
            <Link to="/keys" className="btn-primary text-xs">API keys →</Link>
          </StepCard>
          <StepCard n={5} title="Telegram" done={!!steps?.telegram?.done}>
            <p className="text-[12px] text-slate-500 mb-2">Club extras are own site + Telegram. Add a telegram: destination.</p>
            <Link to="/webhooks" className="btn-ghost text-xs">Webhooks →</Link>
          </StepCard>
          <StepCard n={6} title={t('setup.clubHome')} done={false}>
            <p className="text-[12px] text-slate-500 mb-2">{t('setup.clubHomeHint')}</p>
            <Link to="/home" className="btn-primary text-xs">{t('setup.openHome')}</Link>
          </StepCard>
          <StepCard n={7} title={t('setup.password')} done={false}>
            <p className="text-[12px] text-slate-500 mb-2">{t('setup.passwordHint')}</p>
            <Link to="/home" className="btn-ghost text-xs">{t('setup.openHome')}</Link>
          </StepCard>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => finish.mutate()}
          disabled={!!o?.completed || !o?.ready}
          title={!o?.ready && !o?.completed ? 'Checklist is not finished' : undefined}
        >
          {o?.completed ? t('setup.complete') : o?.ready ? t('setup.mark') : t('setup.incomplete')}
        </button>
        <Link to={isClubLogin(p) ? '/home' : '/matches'} className="text-xs text-slate-500 hover:text-white">{t('setup.skip')}</Link>
      </div>
    </div>
  );
}

function StepCard({ n, title, done, children }: { n: number; title: string; done: boolean; children: ReactNode }) {
  const { t } = useT();
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-white">{n}. {title}</div>
        <span className={done ? 'badge-good' : 'badge-mute'}>{done ? t('setup.done') : t('setup.todo')}</span>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3">
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}
